import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Inbox, CalendarCheck, Plane, ArrowRight, Building2, TrendingUp, Award, Handshake, Globe2, BadgeCheck, Trophy, Cpu, ShieldCheck, Crown, History as HistoryIcon } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import { Reveal, Overline } from "../Reveal";
import { CountUp } from "../CountUp";

export function AboutIntro() {
  const { t, isRtl } = useLang();
  return (
    <section className="relative py-28 px-6 sm:px-12" data-testid="about-section">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <Overline>{t("aboutsec.overline")}</Overline>
          <h2 className="font-head font-black text-3xl sm:text-5xl text-[#1B2A4E]">{t("aboutsec.title")}</h2>
          <p className="text-[#1B2A4E]/65 mt-6 leading-relaxed">{t("aboutsec.body")}</p>
          <Link to="/about" data-testid="about-more-btn" className="mt-7 inline-flex items-center gap-2 text-sm font-black text-[#E8842C] hover:gap-3.5 transition-all">
            {t("welcome.explore")} <ArrowRight className={`w-4 h-4 ${isRtl ? "rotate-180" : ""}`} />
          </Link>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="relative">
            <div className="rounded-[2rem] overflow-hidden border-4 border-white shadow-2xl shadow-[#1B2A4E]/20">
              <img src="/assets/hero-2.jpg" alt="Hire Host" loading="lazy" className="w-full h-96 object-cover" />
            </div>
            <div className="absolute -bottom-6 -start-4 bg-[#1B2A4E] text-white rounded-2xl px-6 py-4 shadow-xl">
              <div className="font-head font-black text-3xl text-[#F5A623]" dir="ltr"><CountUp to={25} suffix="+" /></div>
              <div className="text-[11px] uppercase tracking-widest text-slate-300">{t("stats.years")}</div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const HOW_ICONS = [Inbox, CalendarCheck, Plane];

export function HowWeWork() {
  const { t } = useLang();
  const steps = t("how.steps");
  return (
    <section className="relative py-28 px-6 sm:px-12 bg-white border-y border-[#1B2A4E]/8" data-testid="how-section">
      <div className="max-w-6xl mx-auto">
        <Reveal className="text-center max-w-2xl mx-auto">
          <div className="flex justify-center"><Overline>{t("how.overline")}</Overline></div>
          <h2 className="font-head font-black text-3xl sm:text-5xl text-[#1B2A4E]">{t("how.title")}</h2>
          <p className="text-[#1B2A4E]/60 mt-4">{t("how.sub")}</p>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6 mt-14 relative">
          <div className="hidden md:block absolute top-10 inset-x-24 h-px border-t-2 border-dashed border-[#E8842C]/40" />
          {steps.map((s, i) => {
            const Icon = HOW_ICONS[i];
            return (
              <Reveal key={i} delay={i * 0.12}>
                <div data-testid={`how-step-${i}`} className="relative text-center group">
                  <div className="w-20 h-20 mx-auto rounded-full bg-white border-2 border-[#E8842C] flex items-center justify-center shadow-lg shadow-[#E8842C]/20 relative z-10 group-hover:scale-110 group-hover:bg-[#E8842C] transition-all duration-300">
                    <Icon className="w-8 h-8 text-[#E8842C] group-hover:text-white transition-colors duration-300" />
                    <span className="absolute -top-2 -end-2 w-7 h-7 rounded-full bg-[#1B2A4E] text-white text-xs font-black flex items-center justify-center">{i + 1}</span>
                  </div>
                  <h3 className="font-head font-bold text-lg text-[#1B2A4E] mt-6 mb-3">{s.t}</h3>
                  <p className="text-sm text-[#1B2A4E]/60 leading-relaxed max-w-sm mx-auto">{s.d}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const HIST_ICONS = [Building2, TrendingUp, Award, Handshake, Globe2, BadgeCheck, Trophy, Cpu, ShieldCheck, Crown];

const HistoryCard = ({ h, Icon, className = "" }) => (
  <div className={`card-light p-6 text-start relative overflow-hidden group hover:-translate-y-1 hover:shadow-xl hover:shadow-[#E8842C]/10 transition-all duration-300 ${className}`}>
    <span className="absolute -bottom-4 -end-2 font-head font-black text-6xl text-[#1B2A4E]/[0.04] group-hover:text-[#E8842C]/10 transition-colors" dir="ltr">{h.year}</span>
    <div className="flex items-start gap-4">
      <div className="shrink-0 w-12 h-12 rounded-xl bg-[#1B2A4E] flex items-center justify-center shadow-lg shadow-[#1B2A4E]/20 group-hover:bg-[#E8842C] group-hover:rotate-6 group-hover:scale-110 transition-all duration-300">
        <Icon className="w-6 h-6 text-white" strokeWidth={1.8} />
      </div>
      <div>
        <h3 className="font-head font-bold text-[#1B2A4E] group-hover:text-[#E8842C] transition-colors">{h.t}</h3>
        <p className="text-sm text-[#1B2A4E]/60 mt-2 leading-relaxed">{h.d}</p>
      </div>
    </div>
  </div>
);

export function History() {
  const { t } = useLang();
  const items = t("history.items");
  return (
    <section className="relative py-28 px-6 sm:px-12 dot-grid overflow-hidden" data-testid="history-section">
      <div className="absolute -top-20 -start-20 w-72 h-72 rounded-full bg-[#E8842C]/10 blur-3xl" />
      <div className="absolute -bottom-20 -end-20 w-72 h-72 rounded-full bg-[#1B2A4E]/8 blur-3xl" />
      <div className="max-w-5xl mx-auto relative">
        <Reveal className="text-center">
          <div className="flex justify-center"><Overline>{t("history.overline")}</Overline></div>
          <h2 className="font-head font-black text-3xl sm:text-5xl text-[#1B2A4E]">{t("history.title")}</h2>
          <div className="mt-6 mb-16 flex justify-center">
            <motion.span
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="w-16 h-16 rounded-full bg-white border-2 border-[#E8842C] shadow-lg shadow-[#E8842C]/20 flex items-center justify-center"
            >
              <HistoryIcon className="w-7 h-7 text-[#E8842C]" />
            </motion.span>
          </div>
        </Reveal>
        <div className="relative">
          <div className="absolute top-0 bottom-0 start-5 md:start-1/2 w-0.5 border-s-2 border-dashed border-[#E8842C]/50" />
          <div className="space-y-10">
            {items.map((h, i) => {
              const Icon = HIST_ICONS[i];
              const left = i % 2 === 0;
              return (
                <Reveal key={h.year} delay={0.05}>
                  <div data-testid={`history-${h.year}`} className={`relative flex gap-6 md:gap-0 items-start ${left ? "md:flex-row" : "md:flex-row-reverse"}`}>
                    <div className={`hidden md:block w-1/2 ${left ? "pe-14" : "ps-14"}`}>
                      <HistoryCard h={h} Icon={Icon} />
                    </div>
                    <div className="relative z-10 shrink-0 w-10 md:w-auto md:absolute md:start-1/2 md:-translate-x-1/2 md:rtl:translate-x-1/2 flex flex-col items-center">
                      <span className="inline-block bg-[#E8842C] text-white font-head font-black text-sm px-3.5 py-2 rounded-full shadow-lg shadow-[#E8842C]/30 ring-4 ring-[#FAF7F2]" dir="ltr">{h.year}</span>
                      <span className={`hidden md:block absolute top-1/2 -translate-y-1/2 w-10 border-t-2 border-dashed border-[#E8842C]/50 ${left ? "end-full" : "start-full"}`} />
                    </div>
                    <div className="md:hidden flex-1">
                      <HistoryCard h={h} Icon={Icon} className="p-5" />
                    </div>
                    <div className="hidden md:block w-1/2" />
                  </div>
                </Reveal>
              );
            })}
          </div>
          <div className="flex justify-center mt-10 relative z-10">
            <span className="w-4 h-4 rounded-full bg-[#1B2A4E] ring-4 ring-[#E8842C]/30" />
          </div>
        </div>
      </div>
    </section>
  );
}

const TEAM_STATS = [
  { key: "temporary", pct: 52 },
  { key: "contract", pct: 80 },
  { key: "direct", pct: 98 },
  { key: "payrolling", pct: 65 },
];

export const TEAM_MEMBERS_LEADERSHIP = [
  { img: "/assets/team-ajaz.jpg", name: "Ajaz Khan", role: "r_founder" },
  { img: "/assets/team-meez.jpg", name: "Meezanurraheman Abdussalam", role: "r_mkt" },
  { img: "/assets/team-ameen.jpg", name: "Mohammad Ameen", role: "r_ta" },
];

export const TEAM_MEMBERS_EXEC = [
  { img: "/assets/team-danish.jpg", name: "Danish Riyaz Khan", role: "r_hrexec" },
  { img: "/assets/team-naushad.jpg", name: "Khan Naushad Alam", role: "r_tms" },
  { img: "/assets/team-afroz.jpg", name: "Mohammad Afroz", role: "r_hrofficer" },
  { img: "/assets/team-arif.jpg", name: "Mohammad Arif Khan", role: "r_hradmin" },
  { img: "/assets/team-gurafn.jpg", name: "Mohammad Gurafn Khan", role: "r_chro" },
  { img: "/assets/team-saleem.jpg", name: "Mohammad Saleem", role: "r_hrgen" },
  { img: "/assets/team-shadab.jpg", name: "Mohammad Shadab", role: "r_training" },
];

export const MemberCard = ({ m, i, t }) => (
  <Reveal delay={i * 0.06}>
    <div data-testid={`team-member-${i}`} className="card-light overflow-hidden group">
      <div className="relative aspect-square overflow-hidden">
        <img src={m.img} alt={m.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#1B2A4E]/80 to-transparent" />
      </div>
      <div className="p-5 border-t-4 border-[#E8842C]">
        <h3 className="font-head font-bold text-[#1B2A4E]">{m.name}</h3>
        <p className="text-xs font-bold text-[#E8842C] uppercase tracking-wider mt-1">{t(`teambehind.${m.role}`)}</p>
      </div>
    </div>
  </Reveal>
);

export function TeamBehindUs() {
  const { t, isRtl } = useLang();
  return (
    <section className="relative py-28 px-6 sm:px-12" data-testid="teambehind-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Overline>{t("teambehind.overline")}</Overline>
            <h2 className="font-head font-black text-3xl sm:text-5xl text-[#1B2A4E]">{t("teambehind.title")}</h2>
            <p className="text-[#1B2A4E]/65 mt-6 leading-relaxed">{t("teambehind.body")}</p>
            <div className="mt-8 space-y-5">
              {TEAM_STATS.map((s) => (
                <div key={s.key} data-testid={`teamstat-${s.key}`}>
                  <div className="flex justify-between text-sm font-bold text-[#1B2A4E] mb-1.5">
                    <span>{t(`teambehind.${s.key}`)}</span>
                    <span className="text-[#E8842C]" dir="ltr"><CountUp to={s.pct} suffix="%" duration={1600} /></span>
                  </div>
                  <div className="h-2.5 rounded-full bg-[#1B2A4E]/8 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${s.pct}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                      className="h-full rounded-full bg-gradient-to-r from-[#C96A10] via-[#E8842C] to-[#F5A623]"
                    />
                  </div>
                </div>
              ))}
            </div>
            <Link to="/team" data-testid="meet-team-btn" className="mt-8 inline-flex items-center gap-2 text-sm font-black text-[#E8842C] hover:gap-3.5 transition-all">
              {t("teambehind.meet_all")} <ArrowRight className={`w-4 h-4 ${isRtl ? "rotate-180" : ""}`} />
            </Link>
          </Reveal>
          <div className="grid grid-cols-3 gap-4">
            {TEAM_MEMBERS_LEADERSHIP.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.1}>
                <div className={`card-light overflow-hidden group ${i === 1 ? "translate-y-8" : ""}`}>
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img src={m.img} alt={m.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#1B2A4E]/90 to-transparent p-3 pt-8">
                      <p className="font-head font-bold text-white text-xs sm:text-sm leading-tight">{m.name}</p>
                      <p className="text-[10px] text-[#F5A623] font-bold mt-0.5">{t(`teambehind.${m.role}`)}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
